import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconType } from 'react-icons';
import { FaDownload, FaEnvelope, FaExternalLinkAlt, FaFacebook, FaMobileAlt } from 'react-icons/fa';
import { isRunningAsPWA } from '../serviceWorkerRegistration';
import MobileInstallInstructions from './MobileInstallInstructions';

// Use a the same Icon component pattern as in Navbar
const Icon = ({ icon, ...props }: { icon: IconType } & React.SVGProps<SVGSVGElement>) => {
  const IconComponent = icon as React.ElementType;
  return <IconComponent {...props} />;
};

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const [showInstallInstructions, setShowInstallInstructions] = useState<boolean>(false);
  const [isPWA, setIsPWA] = useState<boolean>(false);
  const [installPromptEvent, setInstallPromptEvent] = useState<any>(null);
  const [isIOS, setIsIOS] = useState<boolean>(false);

  useEffect(() => {
    // Check if already running as PWA
    setIsPWA(isRunningAsPWA());

    // Check if this is iOS device
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    // For non-iOS devices, handle the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Store the event for later use
      setInstallPromptEvent(e);
      console.log('Install prompt event captured in Footer');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = () => {
    if (isIOS) {
      // For iOS we can't automatically install, show the instructions
      setShowInstallInstructions(true);
      return;
    }

    if (!installPromptEvent) {
      console.log('No install prompt event available');
      // Show manual instructions
      setShowInstallInstructions(true);
      return;
    }

    // Show the install prompt
    console.log('Triggering install prompt from footer');
    installPromptEvent.prompt();

    // Wait for the user to respond to the prompt
    installPromptEvent.userChoice.then((choiceResult: { outcome: string }) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
        setIsPWA(true);
      } else {
        console.log('User dismissed the install prompt');
      }
      // Clear the saved prompt as it can only be used once
      setInstallPromptEvent(null);
    });
  };
  
  return (
    <>
      {showInstallInstructions && (
        <MobileInstallInstructions onClose={() => setShowInstallInstructions(false)} />
      )}
      
      <footer className="bg-gradient-to-r from-[#f2edda] to-[#f7f5e8] pt-6 pb-24 md:pb-6 px-4 mt-8 border-t border-yellow-200 shadow-inner">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Credits & Sources */}
            <div className="space-y-3">
              <h3 className="text-[#4b7a3c] text-lg font-bengali font-semibold">{t('footer.source')}</h3>
              <p className="text-gray-700 text-sm">
                {t('footer.inspired')} {" "}
                {t('footer.content')}
              </p>
              <div className="flex flex-col space-y-2">
                <a 
                  href="https://cdn.assunnahfoundation.org/files/Online-Ramadan-Planner-First-Edition-As-Sunnah-Foundation.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-[#c27260] hover:text-[#a85a4a] transition-colors text-sm"
                >
                  <Icon icon={FaExternalLinkAlt} className="w-3 h-3 mr-1" />
                  {t('footer.pdfCopy')}
                </a>
                <a 
                  href="https://www.rokomari.com/book/451227/ramadan-planner-edition-2025" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-[#c27260] hover:text-[#a85a4a] transition-colors text-sm font-medium"
                >
                  <Icon icon={FaExternalLinkAlt} className="w-3 h-3 mr-1" />
                  {t('footer.buy')}
                </a>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="space-y-3">
              <h3 className="text-[#4b7a3c] text-lg font-bengali font-semibold">{t('footer.disclaimer')}</h3>
              <p className="text-gray-700 text-sm">
                {t('footer.copyright')} {" "}
                {t('footer.encouragement')}
              </p>
              <p className="text-gray-700 text-sm">
                {t('footer.error')}:
                <a 
                  href="mailto:mdjubairahmed@gmail.com" 
                  className="inline-flex items-center text-[#c27260] hover:text-[#a85a4a] transition-colors ml-1"
                >
                  <Icon icon={FaEnvelope} className="w-3 h-3 mr-1" />
                  mdjubairahmed@gmail.com
                </a>
              </p>
            </div>

            {/* Development & Installation */}
            <div className="space-y-5">
              {/* Development Team */}
              <div className="space-y-3">
                <h3 className="text-[#4b7a3c] text-lg font-bengali font-semibold">{t('footer.devTeam')}</h3>
                <div className="flex items-start space-x-2">
                  <div className="flex flex-col">
                    <span className="text-gray-800 font-medium">Md Jubair Ahmed</span>
                    <a 
                      href="https://www.facebook.com/mjubairahmed" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-[#c27260] hover:text-[#a85a4a] transition-colors text-sm"
                    >
                      <Icon icon={FaFacebook} className="w-3 h-3 mr-1" />
                      mjubairahmed
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Installation Section - only show if not already installed */}
              {!isPWA && (
                <div className="space-y-3">
                  <h3 className="text-[#4b7a3c] text-lg font-bengali font-semibold">{t('footer.installApp')}</h3>
                  <p className="text-gray-700 text-sm">
                    {t('footer.installDescription')}
                  </p>
                  <button 
                    onClick={handleInstallClick}
                    className="flex items-center px-4 py-3 bg-[#c27260] hover:bg-[#a85a4a] text-white rounded-md transition-colors text-sm font-medium w-full justify-center shadow-md"
                  >
                    {isIOS ? (
                      <>
                        <Icon icon={FaMobileAlt} className="w-4 h-4 mr-2" />
                        {t('footer.installInstructions')}
                      </>
                    ) : (
                      <>
                        <Icon icon={FaDownload} className="w-4 h-4 mr-2" />
                        {t('footer.installNow')}
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-600 italic">
                    {t('footer.offlineAccess')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer; 